/* Undeniable layer for Horizon.
   - Own header, slide cart, search, quick-add sheet, product form and collection filters on Shopify's AJAX APIs
     (cart/add.js, cart/change.js, cart.js, search/suggest.json, Section Rendering).
   - Performance: one passive scroll listener + one rAF per frame; effects only run near the viewport;
     only transform/opacity are animated; nothing runs when prefers-reduced-motion is set. */
(() => {
  'use strict';
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];

  /* ---------------- Money ---------------- */
  function formatMoney(cents, format) {
    const fmt = format || '${{amount}}';
    const n = Math.round(cents) / 100;
    const group = (v, d, t, s) => {
      const [i, f] = v.toFixed(d).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (f ? s + f : '');
    };
    const out = fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(n, 0, ',', '.');
        case 'amount_with_comma_separator': return group(n, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(n, 0, '.', ',');
        default: return group(n, 2, ',', '.');
      }
    });
    return out.replace(/([.,])00(?!\d)/, '');
  }

  async function withLoading(btn, fn) {
    if (!btn || btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    try { await fn(); btn.classList.add('is-added'); setTimeout(() => btn.classList.remove('is-added'), 1400); }
    catch (e) { /* toast already shown */ }
    finally { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); }
  }

  let toastEl, toastTimer;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'u-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-on'), 2800);
  }

  /* ---------------- Global clicks ---------------- */
  document.addEventListener('click', (e) => {
    const t = e.target;
    const add = t.closest('[data-add]');
    if (add && !add.disabled) {
      e.preventDefault();
      withLoading(add, () => addLines([{ id: add.dataset.add, quantity: 1 }]));
      return;
    }
    const qt = t.closest('[data-quick-toggle]');
    if (qt) {
      e.preventDefault();
      const host = qt.closest('[data-quick-host], .u-card');
      $$('.u-card.is-open, [data-quick-host].is-open').forEach((el) => el !== host && el.classList.remove('is-open'));
      host && host.classList.toggle('is-open');
      return;
    }
    const open = t.closest('[data-dialog-open]');
    if (open) {
      const d = document.getElementById(open.dataset.dialogOpen);
      if (d && d.showModal) { d.showModal(); requestAnimationFrame(() => d.classList.add('is-open')); }
      return;
    }
    const close = t.closest('[data-dialog-close]');
    if (close || (t.matches && t.matches('dialog[data-dialog]'))) {
      const d = close ? close.closest('dialog') : t;
      d.classList.remove('is-open');
      setTimeout(() => d.close(), 250);
      return;
    }
    const unit = t.closest('[data-unit]');
    if (unit) {
      const box = unit.closest('.u-modal__box');
      $$('[data-unit]', box).forEach((b) => b.classList.toggle('is-active', b === unit));
      $$('[data-unit-table]', box).forEach((tb) => (tb.hidden = tb.dataset.unitTable !== unit.dataset.unit));
    }
  });

  /* ---------------- Scroll engine ---------------- */
  const tasks = new Set();
  let queued = false;
  addEventListener('scroll', () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; tasks.forEach((fn) => fn()); });
  }, { passive: true });
  function whileVisible(el, fn) {
    new IntersectionObserver(([en]) => { if (en.isIntersecting) { tasks.add(fn); fn(); } else tasks.delete(fn); }, { rootMargin: '25% 0px' }).observe(el);
  }

  function initReveal(root = document) {
    const els = $$('[data-reveal]:not(.is-in)', root);
    if (REDUCED || window.Shopify && Shopify.designMode) { els.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
    }), { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => io.observe(el));
    const pending = new Set(els);
    const catchUp = () => {
      if (!pending.size) return tasks.delete(catchUp);
      const lim = innerHeight * 0.92;
      pending.forEach((el) => { if (el.getBoundingClientRect().top < lim) { el.classList.add('is-in'); pending.delete(el); } });
    };
    tasks.add(catchUp);
  }

  function initManifesto(root = document) {
    $$('[data-manifesto]', root).forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      el.innerHTML = el.textContent.trim().split(/\s+/).map((w) => {
        const accent = w.startsWith('*');
        return `<span class="u-w${accent ? ' u-w--accent' : ''}">${accent ? w.slice(1) : w}</span>`;
      }).join(' ');
      const words = $$('.u-w', el);
      if (REDUCED) return words.forEach((w) => w.classList.add('is-lit'));
      let lit = 0;
      whileVisible(el, () => {
        const r = el.getBoundingClientRect();
        const p = Math.min(1, Math.max(0, (innerHeight * 0.85 - r.top) / (r.height + innerHeight * 0.35)));
        const n = Math.round(p * words.length);
        if (n === lit) return;
        for (let i = Math.min(n, lit); i < Math.max(n, lit); i++) words[i].classList.toggle('is-lit', i < n);
        lit = n;
      });
    });
  }

  function initKinetic(root = document) {
    if (REDUCED) return;
    $$('[data-kinetic]', root).forEach((row) => {
      const host = row.parentElement, dir = +row.dataset.kinetic;
      whileVisible(host, () => {
        const r = host.getBoundingClientRect();
        const p = (innerHeight - r.top) / (innerHeight + r.height);
        row.style.transform = `translate3d(${(dir * (p * 30 - 15) - 10).toFixed(2)}%,0,0)`;
      });
    });
  }

  function initCounters(root = document) {
    const els = $$('[data-count-to]', root);
    if (REDUCED || (window.Shopify && Shopify.designMode)) return;
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (!en.isIntersecting) return;
      io.unobserve(en.target);
      const el = en.target, to = +el.dataset.countTo, suf = el.dataset.suffix || '';
      const t0 = performance.now();
      const step = (t) => {
        const k = Math.min(1, (t - t0) / 1400);
        el.textContent = Math.round(to * (1 - Math.pow(1 - k, 4))) + suf;
        if (k < 1) requestAnimationFrame(step);
      };
      el.textContent = '0' + suf;
      requestAnimationFrame(step);
    }), { threshold: 0.5 });
    els.forEach((el) => io.observe(el));
  }

  /* ---------------- Product rail ---------------- */
  function initRails(root = document) {
    $$('[data-u-rail]', root).forEach((sec) => {
      const track = $('[data-rail-track]', sec), bar = $('[data-rail-bar]', sec);
      if (!track) return;
      const update = () => {
        if (!bar) return;
        const max = track.scrollWidth - track.clientWidth;
        const w = Math.min(100, (track.clientWidth / Math.max(1, track.scrollWidth)) * 100);
        bar.style.width = w + '%';
        bar.style.transform = `translateX(${max > 0 ? (track.scrollLeft / max) * (10000 / w - 100) : 0}%)`;
      };
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      addEventListener('resize', update);
      update();
      sec.addEventListener('click', (e) => {
        const f = e.target.closest('[data-rail-filter]');
        if (f) {
          $$('[data-rail-filter]', sec).forEach((x) => x.classList.toggle('is-active', x === f));
          const q = f.dataset.railFilter;
          $$('[data-match]', track).forEach((c) => (c.hidden = !!q && !c.dataset.match.includes(q)));
          track.scrollLeft = 0; update();
        }
        if (e.target.closest('[data-rail-next]')) track.scrollBy({ left: track.clientWidth * 0.75, behavior: 'smooth' });
        if (e.target.closest('[data-rail-prev]')) track.scrollBy({ left: -track.clientWidth * 0.75, behavior: 'smooth' });
      });
    });
  }

  /* ---------------- 3-pack builder (home) ---------------- */
  const icon = (n) => ({
    plus: '<svg class="u-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>',
    close: '<svg class="u-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    check: '<svg class="u-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>',
  }[n]);
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const defaultVariant = (p) => (p.variants.find((v) => v.available && v.size === 'L') || p.variants.find((v) => v.available) || p.variants[0]);

  function initBundle(root = document) {
    $$('[data-u-bundle]', root).forEach((sec) => {
      const goal = +sec.dataset.goal || 3, pct = +sec.dataset.pct || 20, fmt = sec.dataset.money;
      let pool = [], defaults = [];
      try { pool = JSON.parse($('[data-b2-products]', sec).textContent || '[]'); } catch (e) {}
      try { defaults = JSON.parse($('[data-b2-defaults]', sec).textContent || '[]').filter(Boolean); } catch (e) {}
      const byId = new Map([...pool, ...defaults].map((p) => [p.id, p]));
      let picks = (defaults.length ? defaults : pool.slice(0, goal)).slice(0, goal).map((p) => ({ p, v: defaultVariant(p).id }));
      let filter = 'all';
      const slotsEl = $('[data-b2-slots]', sec), sumEl = $('[data-b2-sum]', sec), stripEl = $('[data-b2-strip]', sec);

      const render = () => {
        slotsEl.innerHTML = Array.from({ length: goal }, (_, i) => {
          const s = picks[i];
          if (!s) return `<button type="button" class="u-b2slot u-b2slot--empty" data-b2-focus><i>${icon('plus')}</i>Pick item ${i + 1}</button>`;
          return `<div class="u-b2slot">
            <div class="u-b2slot__img"><span class="u-b2slot__n">${i + 1}</span><img src="${s.p.img}" alt="" loading="lazy" decoding="async"><button type="button" class="u-b2slot__x" data-b2-remove="${i}" aria-label="Remove ${esc(s.p.title)}">${icon('close')}</button></div>
            <div class="u-b2slot__t"><b>${esc(s.p.title)}</b><span>${esc(s.p.color)}${s.p.color ? ' · ' : ''}${formatMoney(s.p.price, fmt)}</span></div>
            <select data-b2-size="${i}" aria-label="Size for ${esc(s.p.title)}">${s.p.variants.map((v) => `<option value="${v.id}" ${v.id === s.v ? 'selected' : ''} ${v.available ? '' : 'disabled'}>${esc(v.size)}</option>`).join('')}</select>
          </div>`;
        }).join('');
        const n = picks.length, full = picks.reduce((t, s) => t + s.p.price, 0);
        const on = n >= goal, save = on ? full * pct / 100 : 0;
        sumEl.innerHTML = `
          <h3>Your ${goal}-pack</h3>
          <div class="u-b2__pips">${Array.from({ length: goal }, (_, i) => `<i class="${i < n ? 'is-on' : ''}"></i>`).join('')}</div>
          <div class="u-b2__row"><span>${n} of ${goal} picked</span><span>${formatMoney(full, fmt)}</span></div>
          <div class="u-b2__row u-b2__row--save"><span>Buy ${goal}, save ${pct}%</span><span>${on ? '−' + formatMoney(save, fmt) : `${goal - n} to go`}</span></div>
          <div class="u-b2__total"><span>Total</span><div>${on ? `<s>${formatMoney(full, fmt)}</s>` : ''}<b>${formatMoney(full - save, fmt)}</b></div></div>
          <button type="button" class="u-btn u-btn--primary u-btn--block" data-b2-add ${n ? '' : 'disabled'}>${on ? `Add ${goal}-pack to bag` : `Add ${n} to bag`}</button>
          <p class="u-b2__note">${on ? `That's ${formatMoney((full - save) / n, fmt)} per piece.` : `Add ${goal - n} more to unlock ${pct}% off.`}</p>`;
        const inPack = new Set(picks.map((s) => s.p.id));
        stripEl.innerHTML = pool.filter((p) => filter === 'all' || p.kind === filter).map((p) => `
          <button type="button" class="u-b2pick ${inPack.has(p.id) ? 'is-in' : ''}" data-b2-pick="${p.id}" aria-label="Add ${esc(p.title)} to pack">
            <span class="u-ui"><img src="${p.img}" alt="" loading="lazy" decoding="async"><i>${inPack.has(p.id) ? icon('check') : icon('plus')}</i></span>
            <b>${esc(p.title)}</b><span>${esc(p.color)}</span>
          </button>`).join('');
      };

      sec.addEventListener('click', (e) => {
        const t = e.target;
        const rm = t.closest('[data-b2-remove]');
        if (rm) { picks.splice(+rm.dataset.b2Remove, 1); return render(); }
        const pk = t.closest('[data-b2-pick]');
        if (pk) {
          if (picks.length >= goal) return toast(`Your ${goal}-pack is full. Remove one to swap.`);
          const p = byId.get(+pk.dataset.b2Pick);
          if (p) { picks.push({ p, v: defaultVariant(p).id }); render(); }
          return;
        }
        const f = t.closest('[data-b2-filter]');
        if (f) { filter = f.dataset.b2Filter; $$('[data-b2-filter]', sec).forEach((x) => x.classList.toggle('is-active', x === f)); return render(); }
        if (t.closest('[data-b2-focus]')) return stripEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const addBtn = t.closest('[data-b2-add]');
        if (addBtn) withLoading(addBtn, () => addLines(picks.map((s) => ({ id: s.v, quantity: 1 }))));
      });
      sec.addEventListener('change', (e) => { const i = e.target.dataset.b2Size; if (i !== undefined) picks[+i].v = +e.target.value; });
      render();
    });
  }

  /* ---------------- Row-based pack builders (shop the look + product page) ---------------- */
  function initRowBuilder(sec, sel) {
    const goal = +sec.dataset.goal || 3, pct = +sec.dataset.pct || 20, fmt = sec.dataset.money;
    const rows = () => $$(sel.row, sec);
    const update = () => {
      const on = rows().filter((r) => $(sel.toggle, r).checked);
      rows().forEach((r) => r.classList.toggle('is-off', !$(sel.toggle, r).checked));
      const full = on.reduce((t, r) => t + +r.dataset.price, 0);
      const unlocked = on.length >= goal, pay = unlocked ? full * (100 - pct) / 100 : full;
      $(sel.total, sec).textContent = formatMoney(pay, fmt);
      $(sel.was, sec).textContent = unlocked ? formatMoney(full, fmt) : '';
      $(sel.note, sec).textContent = unlocked ? `${pct}% off · you save ${formatMoney(full - pay, fmt)}` : `Add ${goal - on.length} more to save ${pct}%`;
      $(sel.add, sec).textContent = `Add ${on.length} to bag`;
    };
    sec.addEventListener('change', update);
    sec.addEventListener('click', (e) => {
      const btn = e.target.closest(sel.add);
      if (!btn) return;
      const lines = rows().filter((r) => $(sel.toggle, r).checked).map((r) => ({ id: $(sel.variant, r).value, quantity: 1 }));
      withLoading(btn, () => addLines(lines));
    });
    update();
  }

  function initLook(root = document) {
    $$('[data-u-look]', root).forEach((sec) => {
      if ($('[data-look-items]', sec).children.length) {
        initRowBuilder(sec, { row: '[data-look-row]', toggle: '[data-look-toggle]', variant: '[data-look-variant]', total: '[data-look-total]', was: '[data-look-was]', note: '[data-look-note]', add: '[data-look-add]' });
      }
      const media = $('[data-look-media]', sec);
      const hl = (i) => {
        $$('[data-hotspot]', sec).forEach((h) => h.classList.toggle('is-active', +h.dataset.hotspot === i));
        $$('[data-hotspot-card]', sec).forEach((c) => c.classList.toggle('is-on', +c.dataset.hotspotCard === i));
        $$('[data-look-row]', sec).forEach((r) => r.classList.toggle('is-hl', +r.dataset.lookRow === i));
      };
      const place = () => $$('[data-hotspot-card]', sec).forEach((card) => {
        const W = media.clientWidth, H = media.clientHeight;
        const x = (+card.dataset.x / 100) * W, y = (+card.dataset.y / 100) * H;
        const cw = card.offsetWidth || 220, ch = card.offsetHeight || 70;
        card.style.left = (x + 26 + cw > W - 8 ? Math.max(8, x - 26 - cw) : x + 26) + 'px';
        card.style.top = Math.min(H - ch - 8, Math.max(8, y - ch / 2)) + 'px';
      });
      place(); addEventListener('resize', place);
      media.addEventListener('click', (e) => {
        const h = e.target.closest('[data-hotspot]');
        if (h) { e.preventDefault(); hl(h.classList.contains('is-active') ? -1 : +h.dataset.hotspot); }
        else if (!e.target.closest('[data-hotspot-card]')) hl(-1);
      });
    });
  }

  function initPdpBundle(root = document) {
    $$('[data-u-pdp-bundle]', root).forEach((sec) => initRowBuilder(sec, { row: '[data-b-row]', toggle: '[data-b-toggle]', variant: '[data-b-variant]', total: '[data-b-total]', was: '[data-b-was]', note: '[data-b-note]', add: '[data-b-add]' }));
  }

  /* ======================================================================
     STORE CHROME — header, panels, slide cart, search, quick-add sheet,
     collection filters, product form. Real Shopify AJAX APIs throughout.
     ====================================================================== */
  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const icons = {
    plus: '<svg class="u-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>',
    minus: '<svg class="u-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14"/></svg>',
    close: '<svg class="u-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    arrow: '<svg class="u-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>',
    bag: '<svg class="u-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 8h14l-1 13H6L5 8z"/><path d="M9 8V6a3 3 0 016 0v2"/></svg>',
    lock: '<svg class="u-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><rect x="5" y="11" width="14" height="10" rx="3"/><path d="M8 11V8a4 4 0 018 0v3"/></svg>',
    shield: '<svg class="u-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><path d="M12 3l8 3v6c0 4.5-3.4 8-8 9-4.6-1-8-4.5-8-9V6l8-3z"/><path d="M9 12l2 2 4-4" stroke-linecap="round"/></svg>',
  };

  /* ---------- Panels (drawer, menu, search, sheet, filters) ---------- */
  let lastFocus = null;
  const overlayEl = () => $('.u-overlay');
  function openPanel(el, focusSel) {
    if (!el) return;
    lastFocus = document.activeElement;
    $$('.u-drawer.is-open, .u-menu.is-open, .u-search.is-open, .u-sheet.is-open, .u-filters.is-open').forEach((p) => p !== el && p.classList.remove('is-open'));
    el.classList.add('is-open');
    overlayEl() && overlayEl().classList.add('is-open');
    document.documentElement.classList.add('u-no-scroll');
    setTimeout(() => { const f = focusSel ? $(focusSel, el) : el; if (f === el) el.setAttribute('tabindex', '-1'); f && f.focus({ preventScroll: true }); }, 250);
  }
  function closePanels() {
    $$('.u-drawer.is-open, .u-menu.is-open, .u-search.is-open, .u-sheet.is-open, .u-filters.is-open').forEach((p) => p.classList.remove('is-open'));
    overlayEl() && overlayEl().classList.remove('is-open');
    document.documentElement.classList.remove('u-no-scroll');
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    closePanels();
    $$('dialog[data-dialog][open]').forEach((d) => { d.classList.remove('is-open'); d.close(); });
  });

  /* ---------- Header scroll states ---------- */
  function initHeader() {
    const wrap = $('[data-header-wrap]');
    if (!wrap) return;
    const h = wrap.offsetHeight;
    document.documentElement.style.setProperty('--u-header-h', h + 'px');
    let lastY = scrollY, scrolled = null, hidden = false;
    const tick = () => {
      const y = scrollY;
      const s = y > 40, hd = y > 500 && y > lastY + 2 ? true : y < lastY - 2 ? false : hidden;
      if (s !== scrolled) wrap.classList.toggle('is-scrolled', (scrolled = s));
      if (hd !== hidden) wrap.classList.toggle('is-hidden', (hidden = hd));
      lastY = y;
    };
    tasks.add(tick); tick();
  }

  /* ---------- Cart (AJAX API) ---------- */
  const drawer = () => $('[data-drawer]');
  let cart = null;
  try { cart = JSON.parse(($('[data-cart-json]') || {}).textContent || 'null'); } catch (e) {}
  let upsellPool = [];
  try { upsellPool = JSON.parse(($('[data-upsell-json]') || {}).textContent || '[]'); } catch (e) {}

  async function fetchCart() {
    const r = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } });
    cart = await r.json();
    return cart;
  }
  async function cartRequest(path, body) {
    const r = await fetch(ROOT + path, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.description || data.message || 'Could not update your bag');
    return data;
  }
  function goalOf() { const d = drawer(); return d ? +d.dataset.goal || 3 : 3; }
  function pctOf() { const d = drawer(); return d ? +d.dataset.pct || 20 : 20; }

  async function addLines(lines, { open = true } = {}) {
    const items = lines.filter((l) => l.id && l.quantity > 0).map((l) => ({ id: +l.id, quantity: +l.quantity }));
    if (!items.length) return;
    const before = cart ? cart.item_count : 0;
    try {
      await cartRequest('cart/add.js', { items });
      await fetchCart();
      renderCart({ bump: true });
      if (!drawer()) { location.href = ROOT + 'cart'; return; }
      if (open) openPanel(drawer());
      if (before < goalOf() && cart.item_count >= goalOf()) setTimeout(() => toast(`${pctOf()}% off unlocked. Applied automatically.`), open ? 700 : 0);
      document.dispatchEvent(new CustomEvent('u:cart:updated', { detail: cart }));
    } catch (err) {
      toast(err.message || 'Something went wrong. Please try again.');
      throw err;
    }
  }
  async function changeLine(key, quantity) {
    try {
      cart = await cartRequest('cart/change.js', { id: key, quantity });
      renderCart({ bump: true });
      document.dispatchEvent(new CustomEvent('u:cart:updated', { detail: cart }));
    } catch (err) { toast(err.message); }
  }

  function renderCart({ bump = false } = {}) {
    const d = drawer();
    if (!d || !cart) return;
    const fmt = d.dataset.money, goal = goalOf(), pct = pctOf();
    const count = cart.item_count, left = Math.max(0, goal - count);
    $$('[data-count]').forEach((el) => {
      el.textContent = count;
      if (bump) { el.classList.remove('bump'); void el.offsetWidth; el.classList.add('bump'); }
    });
    const label = $('[data-count-label]', d);
    if (label) label.textContent = `${count} item${count === 1 ? '' : 's'}`;

    // Meter
    const meter = $('[data-meter]', d);
    const prevOn = $$('.u-meter__steps i.is-on', meter).length;
    const saving = cart.total_discount || 0;
    meter.classList.toggle('is-unlocked', !left);
    meter.innerHTML = `
      <div class="u-meter__msg">
        <span>${left ? `Add <b>${left} more item${left > 1 ? 's' : ''}</b> to unlock ${pct}% off` : `<b>${pct}% off unlocked.</b>${saving ? ` You're saving ${formatMoney(saving, fmt)}` : ''}`}</span>
        <em>${left ? `${count} / ${goal}` : 'Unlocked'}</em>
      </div>
      <div class="u-meter__steps">${Array.from({ length: goal }, (_, i) => `<i class="${i < prevOn ? 'is-on' : ''}"></i>`).join('')}</div>
      <div class="u-meter__labels"><span>1 item</span><span>2 items</span><span>${goal}+ · ${pct}% off</span></div>`;
    requestAnimationFrame(() => requestAnimationFrame(() => $$('.u-meter__steps i', meter).forEach((el, i) => el.classList.toggle('is-on', i < count))));

    // Upsell
    const inCart = new Set(cart.items.map((i) => i.product_id));
    const upsell = upsellPool.filter((p) => !inCart.has(p.id)).slice(0, 4);
    const discounted = left <= 1;
    const each = (p) => p.price * (100 - pct) / 100;
    const upsellHtml = upsell.length ? `
      <div class="u-upsell">
        <div class="u-upsell__head"><h3>${left ? `Complete your ${goal}-pack` : 'Add to your order'}</h3><span>${left ? `${left} to go` : `${formatMoney(each(upsell[0]), fmt)} each`}</span></div>
        <div class="u-upsell__row">${upsell.map((p) => `
          <div class="u-upsell__item">
            <a class="u-ui" href="${p.url}"><img src="${p.img}" alt="" loading="lazy"></a>
            <div>
              <b>${esc(p.title)}</b>
              <span>${discounted ? `${formatMoney(each(p), fmt)} <s style="color:var(--u-ink-3);font-weight:400">${formatMoney(p.price, fmt)}</s>` : formatMoney(p.price, fmt)}</span>
              <div class="u-upsell__sizes">${p.variants.filter((v) => v.available).slice(0, 4).map((v) => `<button type="button" data-add="${v.id}" aria-label="Add ${esc(p.title)} size ${esc(v.size)}">${esc(v.size)}</button>`).join('')}</div>
            </div>
          </div>`).join('')}
        </div>
      </div>` : '';

    const lines = $('[data-lines]', d);
    if (!count) {
      lines.innerHTML = `
        <div class="u-empty">
          <div class="u-empty__icon">${icons.bag}</div>
          <h3 class="u-display" style="font-size:28px">Your bag is empty</h3>
          <p>Pick any ${goal} tees or shorts and ${pct}% comes off automatically.</p>
          <a class="u-btn u-btn--primary" href="${d.dataset.emptyLink}">Shop all ${icons.arrow}</a>
        </div>${upsellHtml}`;
    } else {
      lines.innerHTML = cart.items.map((it, idx) => {
        const was = it.original_line_price, now = it.final_line_price;
        const size = (it.options_with_values || []).filter((o) => !/colou?r/i.test(o.name)).map((o) => o.value).join(' / ');
        const colorOpt = (it.options_with_values || []).find((o) => /colou?r/i.test(o.name));
        const meta = [colorOpt && colorOpt.value, size].filter(Boolean).join(' / ') || (it.variant_title || '');
        const img = it.image ? it.image.replace(/(\.[a-z]+)(\?.*)?$/i, '$1') + '?width=240' : '';
        return `
        <div class="u-line" style="--i:${idx}" data-line="${it.key}" data-qty="${it.quantity}">
          <a class="u-line__img" href="${it.url}">${img ? `<img src="${img}" alt="" decoding="async">` : ''}</a>
          <div class="u-line__body">
            <div>
              <div class="u-line__top"><a href="${it.url}">${esc(it.product_title)}</a><div class="u-line__price">${formatMoney(now, fmt)}${now < was ? `<s>${formatMoney(was, fmt)}</s>` : ''}</div></div>
              <div class="u-line__meta">${esc(meta)}</div>
            </div>
            <div class="u-line__bottom">
              <div class="u-qty"><button type="button" data-line-qty="-1" aria-label="Decrease quantity">${icons.minus}</button><span>${it.quantity}</span><button type="button" data-line-qty="1" aria-label="Increase quantity">${icons.plus}</button></div>
              <button type="button" class="u-remove" data-line-remove>Remove</button>
            </div>
          </div>
        </div>`;
      }).join('') + upsellHtml;
    }

    const trust = `<div class="u-trust"><span>${icons.shield} ${esc(d.dataset['trust-1'] || '')}</span><span>${icons.lock} ${esc(d.dataset['trust-2'] || '')}</span></div>`;
    $('[data-foot]', d).innerHTML = count ? `
      <div class="u-sum"><span>Subtotal</span><span>${formatMoney(cart.original_total_price || cart.items_subtotal_price, fmt)}</span></div>
      ${saving ? `<div class="u-sum u-sum--save"><span>Buy ${goal}, save ${pct}%</span><span>−${formatMoney(saving, fmt)}</span></div>` : ''}
      <div class="u-sum"><span>Shipping</span><span>Calculated at checkout</span></div>
      <div class="u-sum u-sum--total ${bump ? 'bump' : ''}"><span>Total</span><span>${formatMoney(cart.total_price, fmt)}</span></div>
      <form action="${d.dataset.cartUrl}" method="post"><button type="submit" name="checkout" class="u-btn u-btn--primary u-btn--block">Checkout ${icons.lock}</button></form>
      ${trust}` : trust;
  }

  /* ---------- Quick-add sheet (mobile) ---------- */
  const productCache = new Map();
  async function openSheet(handle, fit) {
    const sheet = $('[data-sheet]');
    if (!sheet) return;
    const fmt = sheet.dataset.money, pct = +sheet.dataset.pct || 20, goal = +sheet.dataset.goal || 3;
    let p = productCache.get(handle);
    if (!p) {
      const r = await fetch(`${ROOT}products/${handle}.js`);
      p = await r.json(); productCache.set(handle, p);
    }
    const sizeIdx = p.options.findIndex((o) => /size/i.test(o.name || o));
    const colorIdx = p.options.findIndex((o) => /colou?r/i.test(o.name || o));
    const color = colorIdx >= 0 ? p.variants[0].options[colorIdx] : '';
    const img = p.featured_image ? p.featured_image.replace(/(\.[a-z]+)(\?.*)?$/i, '$1') + '?width=200' : '';
    sheet.innerHTML = `
      <div class="u-sheet__grab"></div>
      <div class="u-sheet__head">
        <div class="u-ui">${img ? `<img src="${img}" alt="">` : ''}</div>
        <div><b>${esc(p.title)}</b><span>${formatMoney(p.price, fmt)}</span><small>${formatMoney(p.price * (100 - pct) / 100, fmt)} each when you buy ${goal}</small></div>
        <button class="u-icon-btn u-icon-btn--outline" type="button" data-close-panels aria-label="Close">${icons.close}</button>
      </div>
      <div class="u-sheet__label"><span>Select size${color ? ' · ' + esc(color) : ''}</span><span>${esc(fit || '')}</span></div>
      <div class="u-sheet__sizes">${p.variants.map((v) => `<button type="button" data-add="${v.id}" ${v.available ? '' : 'disabled'}>${esc(sizeIdx >= 0 ? v.options[sizeIdx] : v.title)}</button>`).join('')}</div>
      <div class="u-sheet__foot"><a class="u-link-arrow" href="${p.url}">View full details ${icons.arrow}</a></div>`;
    openPanel(sheet);
  }

  /* ---------- Predictive search ---------- */
  function initSearch() {
    const panel = $('[data-search]');
    if (!panel) return;
    const input = $('[data-search-input]', panel), grid = $('[data-search-grid]', panel), label = $('[data-search-label]', panel), all = $('[data-search-all]', panel);
    const trending = grid.innerHTML, fmt = panel.dataset.money;
    let ctrl, timer;
    const run = async (q) => {
      q = q.trim();
      if (!q) { grid.innerHTML = trending; label.textContent = 'Trending now'; all.hidden = true; return; }
      ctrl && ctrl.abort(); ctrl = new AbortController();
      try {
        const url = `${panel.dataset.suggestUrl}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=last`;
        const r = await fetch(url, { signal: ctrl.signal });
        const data = await r.json();
        const list = (data.resources && data.resources.results && data.resources.results.products) || [];
        label.textContent = list.length ? `Results for "${q}"` : `No results for "${q}"`;
        grid.innerHTML = list.map((p, i) => {
          const img = p.image ? p.image.replace(/(\.[a-z]+)(\?.*)?$/i, '$1') + '?width=400' : '';
          const cents = Math.round(parseFloat(String(p.price).replace(/[^0-9.]/g, '')) * 100);
          return `<a class="u-s-item" href="${p.url}" style="--i:${i}"><div class="u-ui">${img ? `<img src="${img}" alt="">` : ''}</div><b>${esc(p.title)}</b><span>${isNaN(cents) ? '' : formatMoney(cents, fmt)}</span></a>`;
        }).join('');
        all.hidden = !list.length;
        all.href = `${panel.dataset.searchUrl}?q=${encodeURIComponent(q)}&type=product`;
      } catch (e) { if (e.name !== 'AbortError') label.textContent = 'Search is unavailable right now'; }
    };
    input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(() => run(input.value), 180); });
    panel.addEventListener('click', (e) => { const t = e.target.closest('[data-search-tag]'); if (t) { input.value = t.textContent.trim(); run(input.value); input.focus(); } });
  }

  /* ---------- Product page ---------- */
  function initPdp(root = document) {
    $$('[data-u-pdp]', root).forEach((pdp) => {
      const form = $('[data-pdp-form]', pdp), idInput = $('[data-pdp-variant]', pdp);
      const sizes = $('[data-size-opts]', pdp), err = $('[data-size-error]', pdp), sizeLabel = $('[data-size-label]', pdp);
      const atc = $('[data-atc]', pdp), qtyVal = $('[data-pdp-qty-val]', pdp), qtyInput = $('[data-pdp-qty-input]', pdp);
      const sticky = $('[data-sticky]', pdp), stickySel = $('[data-sticky-size]', pdp);
      let chosen = sizes ? null : idInput.value, qty = 1;
      const single = sizes && $$('button', sizes).length === 1;
      const setVariant = (id) => {
        chosen = id;
        idInput.value = id;
        if (sizes) $$('button', sizes).forEach((b) => { const on = b.dataset.variant === String(id); b.classList.toggle('is-active', on); b.setAttribute('aria-checked', on); if (on) sizeLabel.textContent = '— ' + b.textContent.trim(); });
        if (stickySel) stickySel.value = id;
        if (sizes) { sizes.classList.remove('is-error'); err.classList.remove('is-on'); }
        const btn = sizes && $(`button[data-variant="${id}"]`, sizes);
        const priceEl = $('[data-pdp-price]', pdp);
        if (btn && priceEl) priceEl.textContent = formatMoney(+btn.dataset.price, pdp.dataset.money);
        // keep the 3-pack builder's main row in sync
        const mainSel = $('[data-u-pdp-bundle] [data-b-row] [data-b-variant]', pdp);
        if (mainSel) { mainSel.value = id; mainSel.dispatchEvent(new Event('change', { bubbles: true })); }
        // Shopify dynamic checkout reads the form's id input
      };
      if (single) setVariant($('button', sizes).dataset.variant);
      sizes && sizes.addEventListener('click', (e) => { const b = e.target.closest('button[data-variant]'); if (b && !b.disabled) setVariant(b.dataset.variant); });
      const requireSize = () => {
        if (chosen || !sizes) return true;
        sizes.classList.remove('is-error'); void sizes.offsetWidth; sizes.classList.add('is-error');
        err.classList.add('is-on');
        sizes.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      };
      $$('[data-pdp-qty]', pdp).forEach((b) => b.addEventListener('click', () => { qty = Math.max(1, qty + +b.dataset.pdpQty); if (qtyVal) qtyVal.textContent = qty; qtyInput.value = qty; }));
      form && form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (!requireSize()) return;
        withLoading(atc || e.submitter, () => addLines([{ id: chosen || idInput.value, quantity: qty }]));
      });
      // Dynamic checkout: require a size first
      const express = $('[data-express]', pdp);
      express && express.addEventListener('click', (e) => { if (!chosen) { e.preventDefault(); e.stopPropagation(); requireSize(); } }, true);
      if (stickySel) {
        stickySel.addEventListener('change', () => setVariant(stickySel.value));
        $('[data-sticky-add]', pdp).addEventListener('click', (e) => {
          if (!chosen) { stickySel.focus(); toast('Select a size first'); return; }
          withLoading(e.currentTarget, () => addLines([{ id: chosen, quantity: qty }]));
        });
        const buy = $('[data-buy]', pdp) || form;
        if (buy) new IntersectionObserver(([en]) => sticky.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0)).observe(buy);
      }
    });
  }

  /* ---------- Collection: filters, sort, columns, load more ---------- */
  function initCollection(root = document) {
    $$('[data-u-collection]', root).forEach((wrap) => {
      if (wrap.dataset.ready) return;
      wrap.dataset.ready = '1';
      const sectionId = wrap.dataset.sectionId;
      const host = wrap.closest('.shopify-section') || wrap.parentElement;
      const render = async (url, push = true) => {
        wrap.classList.add('is-loading');
        const u = new URL(url, location.origin);
        u.searchParams.set('section_id', sectionId);
        const html = await (await fetch(u)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-u-collection]');
        const wasOpen = $('.u-filters.is-open', wrap);
        const openDetails = $$('.u-filter', wrap).map((d) => d.open);
        if (fresh) {
          wrap.innerHTML = fresh.innerHTML;
          $$('.u-filter', wrap).forEach((d, i) => { if (openDetails[i] !== undefined) d.open = openDetails[i]; });
          if (wasOpen) $('.u-filters', wrap).classList.add('is-open');
          u.searchParams.delete('section_id');
          if (push) history.pushState({}, '', u);
          boot(wrap, true);
        }
        wrap.classList.remove('is-loading');
      };
      const formUrl = () => {
        const form = $('[data-filters]', wrap);
        const params = new URLSearchParams(new FormData(form));
        for (const [k, v] of [...params]) if (v === '') params.delete(k);
        return `${wrap.dataset.url}?${params}`;
      };
      let t;
      wrap.addEventListener('change', (e) => {
        if (e.target.closest('[data-filters]')) { clearTimeout(t); t = setTimeout(() => render(formUrl()), 250); }
        if (e.target.matches('[data-sort]')) {
          const input = $('[data-sort-input]', wrap);
          if (input) { input.value = e.target.value; render(formUrl()); }
          else { const u = new URL(location.href); u.searchParams.set('sort_by', e.target.value); u.searchParams.delete('page'); render(u.toString()); }
        }
      });
      wrap.addEventListener('click', (e) => {
        const link = e.target.closest('[data-filter-link], [data-clear-filters]');
        if (link) { e.preventDefault(); render(link.href); return; }
        const cols = e.target.closest('[data-cols]');
        if (cols) { $$('[data-cols]', wrap).forEach((b) => b.classList.toggle('is-active', b === cols)); $('[data-grid]', wrap).style.gridTemplateColumns = cols.dataset.cols === '2' ? 'repeat(2, minmax(0, 1fr))' : ''; return; }
        if (e.target.closest('[data-open-filters]')) { openPanel($('.u-filters', wrap)); return; }
        const more = e.target.closest('[data-load-more]');
        if (more) {
          e.preventDefault();
          withLoading(more, async () => {
            const u = new URL(more.href, location.origin); u.searchParams.set('section_id', sectionId);
            const doc = new DOMParser().parseFromString(await (await fetch(u)).text(), 'text/html');
            const grid = $('[data-grid]', wrap);
            $$('[data-grid] > .u-rail__cell', doc).forEach((c) => grid.appendChild(c));
            const pag = $('[data-pagination]', doc);
            $('[data-pagination]', wrap).replaceWith(pag || document.createElement('div'));
            initReveal(grid);
          });
        }
      });
      addEventListener('popstate', () => render(location.href, false));
    });
  }

  /* ---------- Chrome clicks ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (t.closest('[data-open-cart]') && drawer()) { e.preventDefault(); renderCart(); openPanel(drawer()); return; }
    if (t.closest('[data-open-menu]')) { openPanel($('[data-menu]')); return; }
    if (t.closest('[data-open-search]')) { openPanel($('[data-search]'), '[data-search-input]'); return; }
    if (t.closest('[data-close-panels]')) { closePanels(); return; }
    const qs = t.closest('[data-quick-sheet]');
    if (qs) { e.preventDefault(); openSheet(qs.dataset.quickSheet, qs.dataset.fit); return; }
    const line = t.closest('[data-line]');
    if (line) {
      const key = line.dataset.line, q = +line.dataset.qty;
      const step = t.closest('[data-line-qty]');
      if (step) { changeLine(key, Math.max(0, q + +step.dataset.lineQty)); return; }
      if (t.closest('[data-line-remove]')) { line.classList.add('is-removing'); setTimeout(() => changeLine(key, 0), 260); }
    }
  });
  // Keep the bag in sync when the cart changes elsewhere (other tabs, back/forward cache).
  addEventListener('pageshow', (e) => { if (e.persisted) fetchCart().then(() => renderCart()); });

  /* ---------------- Boot (and re-boot when sections change in the theme editor) ---------------- */
  // Lift header panels to <body> so no sticky/transformed ancestor can trap or clip them.
  function portalPanels(root = document) {
    const found = $$('.u-overlay, [data-drawer], [data-sheet], [data-menu], [data-search]', root).filter((el) => el.parentElement !== document.body);
    if (!found.length) return;
    $$('body > [data-u-portal]').forEach((el) => el.remove());
    found.forEach((el) => { el.setAttribute('data-u-portal', ''); document.body.appendChild(el); });
  }
  function boot(root = document, partial = false) {
    initReveal(root); initManifesto(root); initKinetic(root); initCounters(root);
    initRails(root); initBundle(root); initLook(root); initPdpBundle(root); initPdp(root);
    if (!partial) { initCollection(root); }
    if (root === document) { portalPanels(); initHeader(); initSearch(); renderCart(); }
    $$('[data-u-hero], [data-u-about-hero]', root).forEach((h) => requestAnimationFrame(() => h.classList.add('is-in')));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (e) => { boot(e.target); if (e.target.querySelector('[data-drawer]')) { portalPanels(e.target); initHeader(); initSearch(); renderCart(); } });
})();
